import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useActiveCompanyId } from '@/lib/active-company';
import { Input } from './Input';

interface Hit {
  type: 'item' | 'standard' | 'followup';
  id: number;
  title: string;
  excerpt: string;
  link: string;
}

export function SearchBar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const companyId = useActiveCompanyId();

  // Cmd/Ctrl-K to open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useQuery({
    queryKey: ['search', companyId, q],
    queryFn: () =>
      api.get<Hit[]>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: open && !!companyId && q.trim().length >= 2,
  });

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="hidden h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-body-sm text-text-secondary hover:bg-border/40 lg:inline-flex"
        >
          <SearchIcon />
          <span>Search</span>
          <kbd className="ml-2 rounded border border-border bg-surface px-1.5 text-caption font-mono text-text-secondary">
            ⌘K
          </kbd>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-text-primary/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-24 z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 rounded-lg border border-border bg-surface shadow-card-hover"
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          <div className="border-b border-border p-3">
            <Input
              autoFocus
              placeholder="Search items, standards, follow-ups…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {q.trim().length < 2 ? (
              <p className="px-2 py-6 text-center text-body-sm text-text-secondary">
                Type at least 2 characters.
              </p>
            ) : results.isLoading ? (
              <p className="px-2 py-6 text-center text-body-sm text-text-secondary">
                Searching…
              </p>
            ) : results.isError ? (
              <p className="px-2 py-6 text-center text-body-sm text-danger">
                Search failed. Try again.
              </p>
            ) : results.data && results.data.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {results.data.map((r) => (
                  <li key={`${r.type}-${r.id}`}>
                    <Link
                      to={r.link}
                      onClick={() => setOpen(false)}
                      className="flex flex-col gap-0.5 rounded-md px-3 py-2 hover:bg-background"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-label uppercase tracking-[0.08em] text-accent">
                          {r.type}
                        </span>
                        <span className="font-medium">{r.title}</span>
                      </div>
                      <span className="line-clamp-1 text-body-sm text-text-secondary">
                        {r.excerpt}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-2 py-6 text-center text-body-sm text-text-secondary">
                No matches.
              </p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
